"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
require("@services/report-state/layout-engine/layout-engine-utils");
require("@services/report-state/layout-engine/pages-manager");
require("@services/report-state/layout-engine/shadow-manager");
require("@services/report-state/widget-utils");
var widgets_1 = require("@shared/constants/widgets");
var angular_1 = require("angular");
var lodash_1 = __importDefault(require("lodash"));
var PAGE_BREAK_WIDGET_TYPE = widgets_1.PR_WIDGETS.PAGE_BREAK;
var WIDGET_TYPES_WITHOUT_PADDING = [widgets_1.PR_WIDGETS.BLANK_SPACE];
/**
 * Main logic for the layout engine.
 * This done by integrating the following services:
 * 	- pagesManager: updates and keeps track of ui pages.
 * 	- layoutEngineUtils: core logic for updating widgets and their layouts.
 * For widgets that are "chunked", it uses the main widget for layout,
 * but puts the child "chunks" on the pages
 */
function FlowingPagesFactory(
// @ts-ignore
$q, pageLayout, 
/**
 * @todo Remove `chunker`, `hiddenRenderer` and `sectionManager` dependencies.
 * This service should only handle updating the pages in the ui.
 * Have a less specific service, e.g. widget-operations-service.ts,  handle these dependencies.
 * This also removes the circular dependency between prWidgetChunker and prFlowingPages
 */
// @ts-ignore
chunker, 
// @ts-ignore
hiddenRenderer, sectionManager, layoutEngineUtils, widgetUtils, pagesManager, shadowManager) {
    initPages();
    return {
        addWidget: addWidget,
        removeWidgets: removeWidgets,
        switchWidget: switchWidget,
        reflow: reflow,
        reflowWidget: reflowWidget,
        updateWidgetScale: updateWidgetScale,
        updateWidgetLayout: updateWidgetLayout,
        resetPages: initPages,
        /* @todo - Hide these methods */
        getPages: pagesManager.getPages,
        /* @todo - update callers to directly call these methods */
        showWidgetShadows: shadowManager.showWidgetShadows,
        hideWidgetShadows: shadowManager.hideWidgetShadows,
        getWidgetInsertIndex: layoutEngineUtils.getWidgetInsertIndex,
        getWidgetPage: layoutEngineUtils.getWidgetPage,
        ensureWidgetLayout: layoutEngineUtils.ensureWidgetLayout,
        positionWidget: layoutEngineUtils.positionWidget,
        updateWidgetChunks: layoutEngineUtils.updateWidgetChunks,
        getWidget: widgetUtils.getWidget,
        getWidgets: widgetUtils.getWidgets,
        getWidgetsCount: widgetUtils.getWidgetsCount,
        hasReachedWidgetLimit: widgetUtils.hasReachedWidgetLimit,
        updateWidgetPadding: widgetUtils.updateWidgetPadding,
        updateWidgetStyle: widgetUtils.updateWidgetStyle,
        validateWidgetLayout: widgetUtils.validateWidgetLayout,
        startMoving: widgetUtils.startMoving,
        stopMoving: widgetUtils.stopMoving
    };
    /************************************
     *          PUBLIC METHODS          *
     ************************************/
    function initPages() {
        pagesManager.resetPages();
        var widgets = getWidgets();
        return $q.all(widgets.map(function (widget) {
            if (widget.shadow) {
                return;
            }
            return addWidget(widget);
        }));
    }
    /*
     * Initializing a widget and making sure it exists on a page
     */
    function addWidget(widget, pivot, optBefore) {
        return $q.resolve().then(function () {
            layoutEngineUtils.ensureWidgetLayout(widget);
            if (pivot) {
                // @ts-ignore
                return addWidgetPivot(widget, pivot, optBefore);
            }
            else {
                return reflowWidget(widget);
            }
        });
    }
    function removeWidgets(widgets) {
        return $q.resolve().then(function () {
            lodash_1.default.forEach(widgets, removeWidgetFromPage);
            var updatedWidgets = reflow(getWidgets());
            return $q.all(widgets.map(function (widget) {
                return sectionManager.deleteWidget(widget, updatedWidgets);
            }));
        });
    }
    function switchWidget(widgetId, newType) {
        var widget = widgetUtils.getWidget(widgetId);
        if (widget._type !== newType && widget.isChunked) {
            // @ts-ignore
            widget.layout.rowSpan = pageLayout.getRows() / 3;
        }
        widget._type = newType;
        /**
         * @todo consider removing the next two lines from this file.
         * This file should only be concerned with state related to the ui.
         * Consider adding a `switchWidget` method to widget-operations-service
         * which calls flowingPages.switchWidget and these methods.
         */
        hiddenRenderer.removeWidget(widget); // remove from cache
        chunker.removeChunker(widget);
        removeWidgetFromPage(widget);
        layoutEngineUtils.ensureWidgetLayout(widget);
        reflow();
        sectionManager.saveWidget(widget);
        return widget;
    }
    /*
     * Is responsible for positioning a group of widgets
     * It is done in order, while building a new array so
     * widgets float without running into other widgets
     */
    function reflow(widgets) {
        if (widgets === void 0) { widgets = getWidgets(); }
        pagesManager.removeAllPageBreaks();
        var newWidgets = [];
        for (var _i = 0, widgets_2 = widgets; _i < widgets_2.length; _i++) {
            var toMove = widgets_2[_i];
            layoutEngineUtils.positionWidget(newWidgets, toMove);
            addWidgetToPage(toMove);
            newWidgets.push(toMove);
        }
        pagesManager.cleanExtraPages();
        return newWidgets;
    }
    function reflowWidget(widget) {
        layoutEngineUtils.positionWidget(getWidgets(), widget);
        addWidgetToPage(widget);
        pagesManager.cleanExtraPages();
    }
    function updateWidgetScale() {
        var widgets = getWidgets();
        var chunked = false;
        for (var _i = 0, widgets_3 = widgets; _i < widgets_3.length; _i++) {
            var widget = widgets_3[_i];
            chunked = layoutEngineUtils.updateWidgetScale(widget) || chunked;
        }
        // if some were chunked, we have to reflow cause their height might change
        if (chunked) {
            reflow();
        }
    }
    function updateWidgetLayout(widgetId, delta) {
        var widget = widgetUtils.getWidget(widgetId);
        var originalWidgetLayout = lodash_1.default.cloneDeep(widget.layout);
        lodash_1.default.assign(widget.layout, delta);
        if (!lodash_1.default.isEqual(originalWidgetLayout, widget.layout)) {
            reflow();
            sectionManager.saveWidget(widget);
        }
    }
    /************************************
     *          PRIVATE HELPERS         *
     ************************************/
    function getWidgets() {
        return widgetUtils.getWidgets();
    }
    function addWidgetPivot(widget, pivot, optBefore) {
        if (widget._type !== PAGE_BREAK_WIDGET_TYPE) {
            // set the widget row and column to be the same so it collides
            // @ts-ignore
            // @ts-ignore
            widget.layout.row = pivot.layout.row;
        }
        var widgets = getWidgets();
        var fromIndex = widgetUtils.getWidgetIndex(widget, widgets);
        var indexOfPivot = widgetUtils.getWidgetIndex(pivot, widgets);
        var toIndex = indexOfPivot + (optBefore ? 0 : 1);
        if (fromIndex < toIndex) {
            toIndex -= 1;
        }
        widgets.splice(toIndex, 0, widgets.splice(fromIndex, 1)[0]);
        reflow(widgets);
        /**
         * This is needed to ensure the widget is
         * not saved at the end of the widgets array.
         */
        return sectionManager.addWidget(widget, widgets);
    }
    /*
     * Adds the widget to the right page control
     */
    function addWidgetToPage(widget) {
        if (widget.isChunked) {
            return addChunksToPage(widget);
        }
        var layout = widget.layout;
        var pageIdx = layoutEngineUtils.getWidgetPage(widget);
        if (widget._type !== PAGE_BREAK_WIDGET_TYPE && !widget.moving &&
            // @ts-ignore
            // @ts-ignore
            layout.hasOwnProperty('page') && layout.page !== pageIdx) {
            /**
             * If the widget is in the report at a different page,
             * remove the widget to avoid duplicate widgets.
             * But don't do this if the widget is moving because
             * it will cause angular to re-render while moving it.
             */
            removeWidgetFromPage(widget);
        }
        // @ts-ignore
        layout.page = pagesManager.addWidgetToPage(widget, pageIdx);
        // @ts-ignore
        // @ts-ignore
        // @ts-ignore
        layout.row = Math.max(layout.row || 0, layout.page * pageLayout.getRows());
        if (lodash_1.default.includes(WIDGET_TYPES_WITHOUT_PADDING, widget._type)) {
            // @ts-ignore
            widget.layout.padding = '0px';
        }
    }
    function removeWidgetFromPage(widget) {
        if (widget.isChunked) {
            return removeChunksFromPage(widget);
        }
        shadowManager.hideWidgetShadow(widget);
        pagesManager.removeWidget(widget);
    }
    /*
     * This makes sure that all the chunks are correct, and will add them appropriately to the page
     */
    function addChunksToPage(widget) {
        // if we don't have the right number of chunks
        if (layoutEngineUtils.shouldUpdateChunks(widget)) {
            var chunksToRemove = layoutEngineUtils.updateWidgetChunks(widget);
            lodash_1.default.forEach(chunksToRemove, removeWidgetFromPage);
        }
        lodash_1.default.forEach(widget.chunks, addWidgetToPage);
        // make sure to update the parent widget
        // @ts-ignore
        if (widget.chunks.length) {
            // @ts-ignore
            var firstLayout = widget.chunks[0].layout;
            // @ts-ignore
            // @ts-ignore
            widget.layout.page = firstLayout.page;
            // @ts-ignore
            // @ts-ignore
            widget.layout.row = firstLayout.row;
        }
    }
    function removeChunksFromPage(widget) {
        lodash_1.default.each(widget.chunks || [], removeWidgetFromPage);
    }
}
(function (angularModule) {
    angularModule.factory('prFlowingPages', [
        '$q',
        'prPageLayoutService',
        'prWidgetChunkerFactory',
        'prHiddenRenderer',
        'prSectionManager',
        'pr.layoutEngineUtils',
        'pr.widgetUtils',
        'pr.uiPagesManager',
        'pr.shadowManager',
        FlowingPagesFactory
    ]);
})(angular_1.module('qualtrics.pr'));
